import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Package, CheckCircle, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderTracking = () => {
  const [email, setEmail] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API}/orders?email=${encodeURIComponent(email)}`);
      setOrders(response.data);
      setSearched(true);
      if (response.data.length === 0) {
        toast.info('No orders found for this email');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle className="w-6 h-6 text-brand_green" strokeWidth={3} />;
      case 'processing':
        return <Clock className="w-6 h-6 text-brand_yellow" strokeWidth={3} />;
      default:
        return <Package className="w-6 h-6 text-brand_blue" strokeWidth={3} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-brand_green/20 text-brand_green';
      case 'processing':
        return 'bg-brand_yellow/20 text-black';
      default:
        return 'bg-brand_blue/20 text-brand_blue';
    }
  };

  return (
    <div className="bg-off_white min-h-screen py-12" data-testid="order-tracking-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-chewy text-5xl sm:text-7xl text-center mb-12">
          <span className="text-brand_blue">TRACK</span>{' '}
          <span className="text-brand_red">YOUR ORDER</span>
        </h1>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="bg-white border-4 border-black rounded-xl p-6 shadow-hard mb-8">
          <label className="font-nunito font-bold text-lg block mb-4">
            Enter your email to track your orders:
          </label>
          <div className="flex gap-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="flex-grow px-4 py-3 border-2 border-black rounded-lg font-nunito focus:ring-2 focus:ring-brand_blue focus:ring-offset-2"
              data-testid="email-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="bg-brand_blue text-white font-nunito font-bold px-8 py-3 rounded-full border-2 border-black shadow-hard hover:shadow-hard-hover hover:-translate-y-0.5 transition-all disabled:opacity-50"
              data-testid="search-button"
            >
              <Search className="inline-block w-5 h-5" strokeWidth={3} />
            </button>
          </div>
        </form>

        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block w-16 h-16 border-4 border-brand_blue border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Orders List */}
        {!loading && searched && (
          <div className="space-y-6">
            {orders.length === 0 ? (
              <div className="bg-white border-4 border-black rounded-xl p-12 text-center shadow-hard" data-testid="no-orders">
                <div className="bg-brand_yellow w-24 h-24 rounded-full border-4 border-black mx-auto mb-6 flex items-center justify-center">
                  <Package className="w-12 h-12" strokeWidth={3} />
                </div>
                <h3 className="font-chewy text-3xl text-brand_blue mb-4">No Orders Found</h3>
                <p className="font-nunito text-lg text-gray-700 font-semibold">
                  We couldn't find any orders for this email address.
                </p>
              </div>
            ) : (
              orders.map((order) => (
                <div
                  key={order.id}
                  className="bg-white border-4 border-black rounded-xl p-6 shadow-hard"
                  data-testid={`order-${order.id}`}
                >
                  <div className="flex flex-wrap items-center justify-between mb-4">
                    <div>
                      <h3 className="font-chewy text-2xl text-brand_blue mb-1">
                        Order #{order.id.slice(0, 8).toUpperCase()}
                      </h3>
                      <p className="font-nunito text-sm text-gray-600 font-semibold">
                        {new Date(order.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(order.status)}
                      <span
                        className={`font-nunito font-bold px-4 py-2 rounded-full border-2 border-black capitalize ${getStatusColor(
                          order.status
                        )}`}
                      >
                        {order.status}
                      </span>
                    </div>
                  </div>

                  <div className="border-t-2 border-black pt-4 space-y-3">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between font-nunito">
                        <span>
                          {item.name} x {item.quantity}
                        </span>
                        <span className="font-bold">${(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="border-t-2 border-gray-200 pt-3 flex justify-between font-chewy text-xl text-brand_red">
                      <span>Total:</span>
                      <span>${order.total.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="mt-4 p-4 bg-off_white rounded-lg border-2 border-black">
                    <p className="font-nunito font-semibold">
                      <span className="text-gray-600">Delivery Method:</span>{' '}
                      <span className="capitalize">{order.shipping_method}</span>
                    </p>
                    {order.shipping_address && (
                      <p className="font-nunito font-semibold mt-2">
                        <span className="text-gray-600">Address:</span> {order.shipping_address}
                      </p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTracking;